(() => {
  'use strict';

  if (window.__zvHeaderAssetsReady) return;
  window.__zvHeaderAssetsReady = true;

  const HEADER_SELECTOR = '.shopify-section-header';

  class ZinVoltStickyHeader extends HTMLElement {
    connectedCallback() {
      this.header = this.closest(HEADER_SELECTOR) || document.querySelector(HEADER_SELECTOR);
      this.headerWrapper = this.matches('.header-wrapper')
        ? this
        : this.querySelector('.header-wrapper');
      if (!this.header || !this.headerWrapper) return;

      this.isHomepage = document.body.matches('.template-index, .zv-homepage-active');
      this.alwaysSticky = this.dataset.stickyType === 'always' || this.isHomepage;
      this.header.classList.toggle('zv-header-home-always-visible', this.isHomepage);
      if (this.isHomepage) {
        this.header.classList.remove('shopify-section-header-hidden', 'animate');
      }
      this.bounds = { top: 0, bottom: this.header.getBoundingClientRect().bottom };
      this.currentScrollTop = window.scrollY || 0;
      this.preventReveal = false;
      this.frame = 0;
      this.onScrollHandler = this.requestScrollUpdate.bind(this);
      this.preventRevealHandler = () => { this.preventReveal = true; };

      this.addEventListener('preventHeaderReveal', this.preventRevealHandler);
      window.addEventListener('scroll', this.onScrollHandler, { passive: true });
      this.observeBounds();
      this.syncTransparentHeaderState();
    }

    disconnectedCallback() {
      this.removeEventListener('preventHeaderReveal', this.preventRevealHandler);
      window.removeEventListener('scroll', this.onScrollHandler);
      this.boundaryObserver?.disconnect();
      if (this.frame) window.cancelAnimationFrame(this.frame);
    }

    disableScroll() {
      window.removeEventListener('scroll', this.onScrollHandler);
    }

    enableScroll() {
      window.addEventListener('scroll', this.onScrollHandler, { passive: true });
    }

    getTransparentSection() {
      return document.querySelector('main .shopify-section:first-child [data-header-transparent], main .shopify-section [data-header-transparent]');
    }

    getColorScheme() {
      return this.getTransparentSection()?.dataset.headerTransparentColorScheme || '';
    }

    syncTransparentHeaderState() {
      const lightLogo = this.getTransparentSection()?.dataset.headerLightLogo === 'true';
      this.header.classList.toggle('header--light-logo', lightLogo);
    }

    observeBounds() {
      if (!('IntersectionObserver' in window)) return;

      this.boundaryObserver = new IntersectionObserver((entries, observer) => {
        if (entries[0]) this.bounds = entries[0].intersectionRect;
        observer.disconnect();
      });
      this.boundaryObserver.observe(this.header);
    }

    requestScrollUpdate() {
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = 0;
        this.onScroll();
      });
    }

    onScroll() {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const colorScheme = this.getColorScheme();

      if (this.alwaysSticky) {
        if (this.header.classList.contains('color-background-overlay') && scrollTop > this.header.offsetHeight + 1) {
          this.header.classList.remove('color-background-overlay');
          [...this.header.classList].forEach((className) => {
            if (className.startsWith('color-background-') && className !== 'color-background-overlay') {
              this.header.classList.remove(className);
            }
          });
          this.header.classList.add('shopify-section-header-scroll', 'color-background-overlay-hidden');
        } else if (this.header.classList.contains('color-background-overlay-hidden') && scrollTop < this.header.offsetHeight) {
          this.header.classList.add('color-background-overlay');
          if (colorScheme) this.header.classList.add(colorScheme);
          this.header.classList.remove('shopify-section-header-scroll', 'color-background-overlay-hidden');
        }
      }

      if (scrollTop > this.currentScrollTop && scrollTop > this.bounds.bottom) {
        if (!this.preventHide) this.hide();
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.bounds.bottom) {
        if (!this.preventReveal) {
          this.reveal();
        } else {
          window.clearTimeout(this.revealTimer);
          this.revealTimer = window.setTimeout(() => { this.preventReveal = false; }, 66);
          this.hide();
        }
      } else if (scrollTop <= this.bounds.top) {
        this.reset();
      }

      this.currentScrollTop = scrollTop;
    }

    hide() {
      if (this.alwaysSticky) {
        this.header.classList.remove('shopify-section-header-hidden', 'animate');
        this.header.classList.add('shopify-section-header-scroll');
        return;
      }

      this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      this.closeDisclosures();
    }

    reveal() {
      if (this.alwaysSticky) {
        this.header.classList.remove('shopify-section-header-hidden', 'animate');
        this.header.classList.add('shopify-section-header-scroll');
        return;
      }

      this.header.classList.add('shopify-section-header-sticky', 'animate');
      this.header.classList.remove('shopify-section-header-hidden');

      if (this.header.classList.contains('color-background-overlay')) {
        this.header.classList.remove('color-background-overlay');
        [...this.header.classList].forEach((className) => {
          if (className.startsWith('color-background-') && className !== 'color-background-overlay') {
            this.header.classList.remove(className);
          }
        });
        this.header.classList.add('color-background-overlay-hidden');
      }
    }

    reset() {
      const colorScheme = this.getColorScheme();
      if (this.alwaysSticky) {
        this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-scroll', 'animate');
        return;
      }

      this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
      if (this.header.classList.contains('color-background-overlay-hidden')) {
        this.header.classList.add('color-background-overlay');
        if (colorScheme) this.header.classList.add(colorScheme);
        this.header.classList.remove('color-background-overlay-hidden');
      }
    }

    closeDisclosures() {
      if (typeof window.DetailsDisclosure !== 'undefined') {
        this.querySelectorAll('details-disclosure').forEach((disclosure) => disclosure.close());
      }
      this.querySelectorAll('.disclosure__list').forEach((list) => list.setAttribute('hidden', ''));
    }
  }

  if (!customElements.get('sticky-header')) {
    customElements.define('sticky-header', ZinVoltStickyHeader);
  }

  let resizeObserver;
  let surfaceObserver;
  let surfaceFrame = 0;
  let surfaceHeader;
  let photoHero;

  // Presentation follows the real photo boundary, independently of the native
  // menu/scroll classes. Changing the surface never changes header geometry.
  const updateHeaderSurface = () => {
    surfaceFrame = 0;
    if (!surfaceHeader?.isConnected) return;

    const headerRect = surfaceHeader.getBoundingClientRect();
    const heroRect = photoHero?.isConnected ? photoHero.getBoundingClientRect() : null;
    const overPhoto = Boolean(heroRect && heroRect.height > 0 && heroRect.width > 0
      && heroRect.top <= headerRect.bottom && heroRect.bottom > headerRect.bottom);

    surfaceHeader.classList.toggle('zv-header-over-photo', overPhoto);
    surfaceHeader.toggleAttribute('data-zv-surface-ready', true);
    if (overPhoto) {
      surfaceHeader.style.setProperty('--zv-photo-header-remaining', `${Math.max(0, heroRect.bottom - headerRect.top)}px`);
    } else {
      surfaceHeader.style.removeProperty('--zv-photo-header-remaining');
    }
  };

  const requestHeaderSurfaceUpdate = () => {
    if (!surfaceFrame) surfaceFrame = window.requestAnimationFrame(updateHeaderSurface);
  };

  const initializeHeaderSurface = () => {
    surfaceHeader = document.querySelector(HEADER_SELECTOR);
    const firstSection = document.querySelector('#MainContent .shopify-section');
    photoHero = firstSection?.matches('[data-zv-photo-hero]')
      ? firstSection : firstSection?.querySelector('[data-zv-photo-hero]');

    surfaceObserver?.disconnect();
    if (surfaceHeader && 'ResizeObserver' in window) {
      surfaceObserver = new ResizeObserver(requestHeaderSurfaceUpdate);
      surfaceObserver.observe(surfaceHeader);
      if (photoHero) surfaceObserver.observe(photoHero);
    }
    updateHeaderSurface();
  };

  const updateHeaderDimensions = () => {
    const headerSection = document.querySelector(HEADER_SELECTOR);
    if (!headerSection) return;

    const update = () => {
      const wrapper = headerSection.querySelector('.header-wrapper');
      if (!wrapper) return;

      const fullHeight = headerSection.getBoundingClientRect().height;
      const topHeight = headerSection.querySelector('.header__top')?.getBoundingClientRect().height || 0;
      const menuHeight = headerSection.querySelector('.header__inline-menu')?.getBoundingClientRect().height || 0;
      const calculatedHeight = wrapper.classList.contains('header-wrapper--bottom-menu') && !wrapper.classList.contains('header-wrapper--full-width')
        ? topHeight + menuHeight + 12
        : fullHeight;

      document.documentElement.style.setProperty('--header-height', `${calculatedHeight}px`);
      document.documentElement.style.setProperty('--zv-header-height', `${calculatedHeight}px`);
      document.documentElement.style.setProperty('--header-height-top', `${topHeight}px`);
      document.documentElement.style.setProperty('--header-height-bottom', `${Math.max(0, menuHeight - 4)}px`);
    };

    resizeObserver?.disconnect();
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(update);
      resizeObserver.observe(headerSection);
    }
    update();
  };

  const ACCOUNT_HOVER_QUERY = '(hover: hover) and (pointer: fine) and (min-width: 990px)';

  const initializeAccountHover = (scope = document) => {
    const accounts = scope.querySelectorAll?.(
      `${HEADER_SELECTOR} details-disclosure.header__account`
    );
    if (!accounts) return;

    accounts.forEach((account) => {
      if (account.dataset.zvAccountHoverReady === 'true') return;

      const details = account.querySelector('details');
      if (!details) return;

      let closeTimer = 0;
      const canHover = () => window.matchMedia(ACCOUNT_HOVER_QUERY).matches;

      const openAccount = () => {
        if (!canHover()) return;
        window.clearTimeout(closeTimer);
        details.setAttribute('open', '');
      };

      const closeAccount = () => {
        window.clearTimeout(closeTimer);
        closeTimer = window.setTimeout(() => {
          if (account.matches(':hover') || account.contains(document.activeElement)) return;
          details.removeAttribute('open');
        }, 140);
      };

      account.addEventListener('mouseenter', openAccount);
      account.addEventListener('mouseleave', closeAccount);
      account.addEventListener('focusin', openAccount);
      account.addEventListener('focusout', closeAccount);
      account.dataset.zvAccountHoverReady = 'true';
    });
  };

  const initialize = () => {
    updateHeaderDimensions();
    initializeHeaderSurface();
    initializeAccountHover();
  };

  window.addEventListener('scroll', requestHeaderSurfaceUpdate, { passive: true });
  window.addEventListener('resize', requestHeaderSurfaceUpdate, { passive: true });
  window.addEventListener('pageshow', requestHeaderSurfaceUpdate);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      updateHeaderDimensions();
      initializeHeaderSurface();
      initializeAccountHover();
    });
  });
})();
